import { useEffect, useState } from 'react';
import { playbackProfiles, type PlaybackProfileData, type UpsertPlaybackProfileBody } from '@/api/playbackProfiles';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

// ── Helper ────────────────────────────────────────────────────────────────────

const SCOPE_LABEL: Record<string, string> = {
  platform: 'Platform default',
  reseller: 'Reseller override',
  customer: 'Customer override',
};

function scopeBadge(scope: string) {
  return (
    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
      {SCOPE_LABEL[scope] ?? scope}
    </span>
  );
}

// ── Number field ──────────────────────────────────────────────────────────────

function NumField({
  id,
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step ?? 1}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isNaN(n)) onChange(n);
        }}
        className="h-8 w-28 text-sm"
      />
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PlaybackProfilePage() {
  const [profile, setProfile] = useState<PlaybackProfileData | null>(null);
  const [form, setForm] = useState<UpsertPlaybackProfileBody | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    playbackProfiles.get().then((p) => {
      if (ignore) return;
      setProfile(p);
      setForm({
        auto_bookmark: p.auto_bookmark,
        speed_min: p.speed_min,
        speed_max: p.speed_max,
        speed_step: p.speed_step,
        seek_short_sec: p.seek_short_sec,
        seek_long_sec: p.seek_long_sec,
        seek_xlong_sec: p.seek_xlong_sec,
        seek_9_sec: p.seek_9_sec,
        dtmf_mappings: p.dtmf_mappings ?? {},
      });
    }).catch(() => setStatus('Failed to load playback profile.'));
    return () => { ignore = true; };
  }, []);

  function set<K extends keyof UpsertPlaybackProfileBody>(key: K, value: UpsertPlaybackProfileBody[K]) {
    setForm((prev) => prev ? { ...prev, [key]: value } : prev);
    setStatus(null);
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setStatus(null);
    try {
      const updated = await playbackProfiles.put(form);
      setProfile(updated);
      setStatus('Saved.');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Playback Profile"
        description="Configure DTMF seek, speed, and bookmark behaviour for audio playback."
      />

      {!form ? (
        <div className="text-sm text-slate-400">{status ?? 'Loading…'}</div>
      ) : (
        <div className="max-w-2xl space-y-6">
          {/* Scope indicator */}
          {profile && (
            <p className="text-xs text-slate-500">
              Showing effective profile:{scopeBadge(profile.scope)}
              {profile.id === 'default' && (
                <span className="ml-1 text-slate-400">(hard-coded default — save to create an override)</span>
              )}
            </p>
          )}

          {/* Bookmark */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bookmarks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="auto_bookmark"
                  checked={form.auto_bookmark}
                  onCheckedChange={(v) => set('auto_bookmark', v)}
                  aria-label="Auto-bookmark on exit"
                />
                <Label htmlFor="auto_bookmark" className="cursor-pointer">
                  Auto-save position to slot 0 when caller presses * or # (back / return-to-bookmark)
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Speed */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Playback Speed</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
              <NumField
                id="speed_min"
                label="Min speed"
                hint="Slowest allowed (0.25–1.0)"
                value={form.speed_min}
                onChange={(v) => set('speed_min', v)}
                min={0.25} max={1.0} step={0.05}
              />
              <NumField
                id="speed_max"
                label="Max speed"
                hint="Fastest allowed (1.0–4.0)"
                value={form.speed_max}
                onChange={(v) => set('speed_max', v)}
                min={1.0} max={4.0} step={0.25}
              />
              <NumField
                id="speed_step"
                label="Speed step"
                hint="Increment per DTMF press (0.05–1.0)"
                value={form.speed_step}
                onChange={(v) => set('speed_step', v)}
                min={0.05} max={1.0} step={0.05}
              />
            </CardContent>
          </Card>

          {/* Seek */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Seek Durations (seconds)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
              <NumField
                id="seek_short_sec"
                label="Short seek (1 / 3)"
                hint="5–120 s"
                value={form.seek_short_sec}
                onChange={(v) => set('seek_short_sec', v)}
                min={5} max={120}
              />
              <NumField
                id="seek_long_sec"
                label="Long seek (4 / 6)"
                hint="30–600 s"
                value={form.seek_long_sec}
                onChange={(v) => set('seek_long_sec', v)}
                min={30} max={600}
              />
              <NumField
                id="seek_xlong_sec"
                label="X-long seek (7 / 9)"
                hint="60–3600 s"
                value={form.seek_xlong_sec}
                onChange={(v) => set('seek_xlong_sec', v)}
                min={60} max={3600}
              />
              <NumField
                id="seek_9_sec"
                label="DTMF 9 forward"
                hint="30–600 s"
                value={form.seek_9_sec}
                onChange={(v) => set('seek_9_sec', v)}
                min={30} max={600}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {status && (
              <p className="text-sm" role="alert">{status}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
